import { Outlet, createRootRoute, useLocation } from '@tanstack/react-router'
import { MotionConfig } from 'framer-motion'
import { useEffect, useMemo } from 'react'
import { StateProvider } from '@/lib/state-context'
import { buildLivePack } from '@/states'
import SiteHeader from '@/components/SiteHeader'
import SiteFooter from '@/components/SiteFooter'
import StateDetectBanner from '@/components/StateDetectBanner'
import TenantPartnershipBanner from '@/components/state-page/TenantPartnershipBanner'
import UmbrellaHeader from '@/components/umbrella/UmbrellaHeader'
import UmbrellaFooter from '@/components/umbrella/UmbrellaFooter'
import DcucHeader from '@/components/dcuc/DcucHeader'
import DcucFooter from '@/components/dcuc/DcucFooter'
import AmbaHeader from '@/components/amba/AmbaHeader'
import AmbaFooter from '@/components/amba/AmbaFooter'
import GranGuardHeader from '@/components/granguard/GranGuardHeader'
import GranGuardFooter from '@/components/granguard/GranGuardFooter'
import QatarHeader from '@/components/qatar/QatarHeader'
import QatarFooter from '@/components/qatar/QatarFooter'
import { QATAR_BRAND } from '@/components/qatar/brand'
import { COMMUNITY_PARTNER } from '@/lib/community-partners'
import PartnerGate from '@/components/PartnerGate'
import { useDemoFlag } from '@/lib/demo-flags'
import { applyFavicon, applyPageSeo } from '@/lib/page-seo'
import { resolveSite, resolveActiveTenantSlug } from '@/lib/site'
import { applyLocaleToDocument, useLocale } from '@/lib/locale'
import { useTerminology } from '@/lib/useTerminology'
import { useRemoteTenant } from '@/states/useRemoteTenant'

/** Per-route SEO for the apex (umbrella) site. Paths not listed fall back to
 *  the index.html defaults. Source: docs/strategy/messaging-core.md. */
const UMBRELLA_SEO: Record<string, { title: string; description: string }> = {
  '/': {
    // Brand-first and 51 chars so Google shows it verbatim instead of
    // rewriting + truncating (the old 73-char title got cut with an ellipsis).
    title: 'ScamDefense · White-Label Scam Prevention Education',
    description:
      'Launch a branded scam-prevention program for the people you serve: practical training, current scam guidance, and tools for the moment something feels suspicious.',
  },
  '/solutions': {
    title: 'Solutions · ScamDefense',
    description:
      'One scam-prevention platform, tailored for state agencies, banks and credit unions, wealth advisors, and community partners.',
  },
  '/solutions/state-agencies': {
    title: 'ScamDefense for State Agencies · State-branded scam prevention',
    description:
      'A state-branded scam education platform that helps residents recognize scams before it costs them, with reporting routed to your hotlines.',
  },
  '/solutions/financial-institutions': {
    title: 'ScamDefense for Banks & Credit Unions · Member scam prevention',
    description:
      'Branded scam-prevention education that helps account holders pause before money moves. No account access, no transaction monitoring.',
  },
  '/solutions/wealth-advisors': {
    title: 'ScamDefense for Wealth Advisors & RIAs · Protect clients, keep the next generation',
    description:
      'Scam-defense education for clients and their families under your practice brand: protection between meetings and a real next-generation relationship.',
  },
  '/solutions/community-partners': {
    title: 'ScamDefense for Community Partners · Ready-to-share scam prevention',
    description:
      'Ready-to-share scam education, workshop kits, and printable materials for libraries, nonprofits, senior centers, employers, and faith communities.',
  },
  '/dcuc': {
    title: 'OnGuard for DCUC Credit Unions · Scam defense for military families',
    description:
      'A DCUC member benefit you can make your own: online scam-defense training for service members, families and veterans, customized with your credit union’s branding.',
  },
  '/solutions/defense-credit-unions': {
    title: 'ScamDefense for Defense Credit Unions · Protect the military community',
    description:
      'Scam-defense education tailored to the fraud aimed at service members, military families, and veterans, under your credit union brand.',
  },
  '/platform': {
    title: 'Platform · ScamDefense',
    description:
      'Defensive training, trusted guidance, live scam intelligence, and branded resources: the platform behind every ScamDefense deployment.',
  },
  '/how-it-works': {
    title: 'How it works · ScamDefense',
    description:
      'Choose a pilot audience, launch under your brand, and track engagement. A complete scam-prevention program without building one.',
  },
  '/about': {
    title: 'About · ScamDefense',
    description:
      'ScamDefense builds prevention-first scam education for the organizations communities already trust.',
  },
  '/contact': {
    title: 'Contact · ScamDefense',
    description:
      'Tell us who you serve and ask about a branded pilot. We respond within one business day.',
  },
  '/scam-library': {
    title: 'Scam Library · ScamDefense',
    description:
      'A continuously updated library of scam tactics, real examples, and prevention guidance.',
  },
  '/states': {
    title: 'Scam Statistics by State · ScamDefense',
    description:
      'Reported scam losses and the top fraud types in every US state, from FBI IC3 data, with prevention guidance for the people most targeted.',
  },
  '/tools': {
    title: 'Free Scam-Check Tools · ScamDefense',
    description:
      'Free tools for the moment something feels off: an AI scam checker, a password strength check, and a charity checker.',
  },
  '/tools/scam-checker': {
    title: 'AI Scam Checker · ScamDefense',
    description:
      'Paste a suspicious message, link, or situation and get a plain-language read on the risk and what to do next. Free, no signup.',
  },
  '/tools/password-check': {
    title: 'Password Strength Check · ScamDefense',
    description:
      'Check how long a password would take to crack and get practical guidance on making it stronger. Runs in your browser; nothing is sent anywhere.',
  },
  '/tools/charity-checker': {
    title: 'Charity Checker · ScamDefense',
    description:
      'Verify a charity before you give: registration status, revocations, and whether the website or phone that asked you matches their official filing.',
  },
  '/basics': {
    title: 'Scam Basics Course · ScamDefense',
    description:
      'A free six-chapter course on how modern scams work: the pressure tactics, the warning signs, and what to do if one reaches you.',
  },
  '/articles': {
    title: 'Scam Prevention Articles · ScamDefense',
    description:
      'Plain-English guides to how scams work: social engineering, how criminals get your personal information, and what AI changes.',
  },
  '/privacy': {
    title: 'Privacy Policy · ScamDefense',
    description: 'How ScamDefense handles data: what we collect, what we never collect, and your choices.',
  },
  '/terms': {
    title: 'Terms of Service · ScamDefense',
    description: 'The terms that govern use of ScamDefense.',
  },
}

/** Paths whose SEO is owned by the (code-split) route component via
 *  usePageSeo, so their data stays out of the entry bundle. The root
 *  effect must not touch these or it would clobber the route's title
 *  on navigation (parent effects run after child effects). */
const ROUTE_MANAGED_PREFIXES = ['/states/', '/scam-library/', '/articles/', '/basics/']

/** Homepage experiments and apex-orphaned duplicates: reachable but never
 *  indexed, so they can't compete with the homepage or pollute sitelinks.
 *  (/posts duplicates /articles on the apex; it is the NASAA face's list.) */
const NOINDEX_PATHS = new Set(['/home-v2', '/home-v3', '/home-v5', '/v2', '/posts'])

export const Route = createRootRoute({
  component: RootLayout,
})

function RootLayout() {
  const location = useLocation()
  const site = resolveSite()
  const locale = useLocale()

  // `lang` + `dir` on <html> drive Arabic typography and RTL mirroring for
  // the bilingual face; English faces resolve to en/ltr and are untouched.
  useEffect(() => {
    applyLocaleToDocument(locale)
  }, [locale])

  // Per-site institution wording (DCUC: bank → credit union) applied to all
  // rendered text; see useTerminology for why this is a DOM-level pass.
  useTerminology()
  const stateSlug = useMemo(() => {
    const m = location.pathname.match(/^\/states\/([a-z][a-z0-9-]+)(?:\/|$)/i)
    return m ? m[1].toLowerCase() : null
  }, [location.pathname])

  // Tenant slug comes from `?tenant=` (explicit, wins) or the subdomain
  // (`<slug>.scamdefense.org`) for a provisioned member on its own domain.
  const tenantId = useMemo(() => {
    return resolveActiveTenantSlug()
  }, [location.pathname, location.search])

  // Partners provisioned through the portal aren't in the hardcoded tenant map;
  // resolve their theme from the API. Returns null for hardcoded/unknown slugs.
  const remoteTenant = useRemoteTenant(tenantId)

  const pack = useMemo(() => {
    const base = buildLivePack(stateSlug, tenantId)
    // A hardcoded tenant (resolved synchronously inside buildLivePack) wins.
    if (base.tenant) return base
    // Otherwise apply the DB-backed tenant once it arrives.
    if (remoteTenant) return { ...base, tenant: remoteTenant }
    return base
  }, [stateSlug, tenantId, remoteTenant])

  // Hide chrome when a chapter is open (SlideWizard takes over the viewport).
  const isInChapter = /^\/basics\/[^/]+\/start/.test(location.pathname)
  // The partner proposal page (lafayette.granguard.org/pitch) carries its own
  // lockup and footer; the consumer nav would only confuse an exec reader.
  const isPitch = location.pathname === '/pitch'
  // The DCUC newsletter landing page (scamdefense.org/dcuc) also carries its
  // own OnGuard header and footer, continuing the ad it is linked from.
  const isDcucLanding = location.pathname === '/dcuc'
  const hideChrome = isInChapter || isPitch || isDcucLanding
  // Auto-detect banner gated behind ?banner=1 for demos; off by default per
  // NASAA feedback (Apr 2026) — feels too consumer-y on the public B2B build.
  const detectBannerEnabled = useDemoFlag('detectBanner')
  const showDetectBanner = detectBannerEnabled && !isInChapter && stateSlug === null

  // Each face gets its own chrome: umbrella (blue platform brand), DCUC
  // (navy/red military pitch), and NASAA (consumer header + state banners).
  const isNasaa = site === 'nasaa'

  // index.html ships with the umbrella title (apex is primary); set the
  // appropriate title on the subdomains, and per-route titles/descriptions
  // + canonical on the apex so pages don't all share one snippet in search.
  useEffect(() => {
    if (site === 'nasaa') {
      document.title = 'Senior Scam Defense'
    } else if (site === 'dcuc') {
      document.title =
        'ScamDefense for Defense Credit Unions · Fraud protection for the military community'
    } else if (site === 'amba') {
      document.title =
        'ScamDefense for Military Banks · Fraud protection for the military community'
    } else if (site === 'granguard') {
      document.title = COMMUNITY_PARTNER
        ? location.pathname === '/pitch'
          ? `A proposal for ${COMMUNITY_PARTNER.name} · GranGuard`
          : `Scam Defense for ${COMMUNITY_PARTNER.name}`
        : 'GranGuard · Scam defense training and scam library'
      applyFavicon('granguard')
    } else if (site === 'qatar') {
      // A demo face: never indexed, whichever brand preset is showing.
      applyPageSeo({
        title:
          locale === 'ar'
            ? `${QATAR_BRAND.program.ar} · تدريب ومكتبة للحماية من الاحتيال`
            : `${QATAR_BRAND.program.en} · Scam defense training and scam library`,
        canonicalPath: location.pathname,
        noindex: true,
      })
    } else {
      // Dynamic-data routes own their SEO (see ROUTE_MANAGED_PREFIXES); the
      // root only handles the static map, the fallback, and noindexing.
      if (ROUTE_MANAGED_PREFIXES.some((p) => location.pathname.startsWith(p))) return
      const seo = UMBRELLA_SEO[location.pathname]
      applyPageSeo({
        title: seo?.title ?? 'ScamDefense · White-Label Scam Prevention Education',
        description: seo?.description,
        canonicalPath: location.pathname,
        noindex: NOINDEX_PATHS.has(location.pathname),
      })
    }

    // Tenant faces: index.html's static canonical claims every page is the
    // www apex homepage, which is false on subdomains. Self-canonical there.
    if (site !== 'umbrella') {
      let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.rel = 'canonical'
        document.head.appendChild(canonical)
      }
      canonical.href = `${window.location.origin}${location.pathname === '/' ? '/' : location.pathname}`
    }
  }, [site, location.pathname, locale])

  const HEADERS: Record<typeof site, typeof SiteHeader> = {
    umbrella: UmbrellaHeader,
    dcuc: DcucHeader,
    amba: AmbaHeader,
    granguard: GranGuardHeader,
    qatar: QatarHeader,
    nasaa: SiteHeader,
  }
  const FOOTERS: Record<typeof site, typeof SiteFooter> = {
    umbrella: UmbrellaFooter,
    dcuc: DcucFooter,
    amba: AmbaFooter,
    granguard: GranGuardFooter,
    qatar: QatarFooter,
    nasaa: SiteFooter,
  }
  const Header = HEADERS[site]
  const Footer = FOOTERS[site]
  const ContentWrapper = isDcucLanding ? 'div' : 'main'

  return (
    // reducedMotion="user" honors prefers-reduced-motion: framer-motion drops
    // transform/layout animation (the y/scale movement) and keeps opacity only.
    <MotionConfig reducedMotion="user">
      <StateProvider pack={pack}>
        <PartnerGate>
        <div className={`flex min-h-full flex-col ${site === 'dcuc' ? 'theme-dcuc' : site === 'amba' ? 'theme-amba' : site === 'qatar' ? `theme-qatar brand-${QATAR_BRAND.id}` : ''}`}>
          {!hideChrome && <Header />}
          {isNasaa && !isInChapter && <TenantPartnershipBanner />}
          {isNasaa && showDetectBanner && <StateDetectBanner />}
          <ContentWrapper className="flex-1">
            <Outlet />
          </ContentWrapper>
          {!hideChrome && <Footer />}
        </div>
        </PartnerGate>
      </StateProvider>
    </MotionConfig>
  )
}
